/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  forwardRef,
  TemplateRef
} from '@angular/core';
import { DOWN_ARROW, LEFT_ARROW, RIGHT_ARROW, UP_ARROW } from '@angular/cdk/keycodes';
import { Platform } from '@angular/cdk/platform';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject, Subscription, fromEvent, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';

import { PhxSliderHandleComponent } from './phx-handle.component';
import { PhxSliderService } from './phx-slider.service';
import { PhxExtendedMark, PhxMarks, PhxSliderHandler, PhxSliderShowTooltip, PhxSliderValue, PhxFillType, PhxSliderStyle } from './phx-slider.types';
import { PhxTooltipSize } from '../tooltip/phx-tooltip.component';
import { PHX_SIZE } from '../../constants';
import mixinDisabled from '../../mixins/disabled';

const baseMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./phx-slider.component.scss'],
  selector: 'phx-slider',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxSliderComponent),
      multi: true
    },
    PhxSliderService
  ],
  host: {
    '(keydown)': 'onKeyDown($event)',
    'class': 'phx-slider',
    '[class.phx-fill-type-full]': `phxFillType === 'full'`,
    '[class.phx-fill-type-progress]': `phxFillType === 'progress'`,
    '[class.phx-fill-type-none]': `phxFillType === 'none'`,
    '[class.phx-slider-with-marks]': `marksArray && !phxInlineLabel`,
    '[class.phx-slider-disabled]': "disabled",
    '[class.phx-slider-inline]': "phxInlineLabel"
  },
  inputs: [
    'disabled'
  ],
  template: `
    <label *ngIf="phxInlineLabel" class="phx-web-type-body-extra-small phx-slider-labels phx-slider-label-min" [style.width.px]="phxMinLabelWidth">{{ minLabel }}</label>
    <div class="phx-slider-container" #slider>
      <div class="phx-slider-rail" [ngClass]="{ 'phx-fill-type-full': phxFillType === 'full',
      'phx-fill-type-progress': phxFillType === 'progress',
      'phx-fill-type-none': phxFillType === 'none' }"></div>
      <phx-slider-track
        [phxOffset]="track.offset!"
        [phxLength]="phxFillType != 'none' ? track.length! : 0"
      ></phx-slider-track>
      <phx-slider-step
        *ngIf="marksArray"
        [phxMarksArray]="marksArray"
      ></phx-slider-step>
      <phx-slider-handle
        *ngFor="let handle of handles; index as handleIndex"
        [phxOffset]="handle.offset!"
        [phxValue]="handle.value!"
        [phxActive]="handle.active"
        [phxTooltipVisible]="phxTooltipVisible"
        [phxTooltipFormatter]="phxTooltipFormatter"
        [phxTooltipContent]="phxTooltipContent"
        [phxTooltipSize]="phxTooltipSize"
        [disabled]="disabled"
        (focusin)="onHandleFocusIn(handleIndex)"
      ></phx-slider-handle>
      <phx-slider-marks
        *ngIf="!phxInlineLabel"
        [phxMin]="phxMin"
        [phxMax]="phxMax"
        [phxMarksArray]="marksArray"
      ></phx-slider-marks>
    </div>
    <label *ngIf="phxInlineLabel" class="phx-web-type-body-extra-small phx-slider-labels" [style.width.px]="phxMaxLabelWidth">{{ maxLabel }}</label>
  `
})
export class PhxSliderComponent extends baseMixin implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

  static ngAcceptInputType_disabled: boolean;
  static ngAcceptInputType_phxRange: boolean;
  static ngAcceptInputType_phxMax: number;
  static ngAcceptInputType_phxMin: number;
  static ngAcceptInputType_phxStep: number;

  @ViewChild('slider', { static: true }) slider!: ElementRef<HTMLDivElement>;
  @ViewChildren(PhxSliderHandleComponent) handlerComponents!: QueryList<PhxSliderHandleComponent>;

  @Input() disabled = false;
  @Input() phxRange: boolean = false;
  @Input() phxMarks: PhxMarks | null = null;
  @Input() phxMax: number = 100;
  @Input() phxMin: number = 0;
  @Input() phxStep: number = 1;
  @Input() phxTooltipVisible: PhxSliderShowTooltip = 'hover';
  // TODO: Remove phxGetLabelsFromPhxMarks as it is a temporary workaround for backward compatibility considering how `phxMarks` is used in our codebase:
  @Input() phxGetLabelsFromPhxMarks: boolean = false;
  @Input() phxInlineLabel: boolean = false;
  @Input() phxMinLabelWidth: number;
  @Input() phxMaxLabelWidth: number;
  /**
   * @deprecated Use `phxTooltipContent` instead.
   */
  @Input() phxTooltipFormatter?: null | ((value: number) => string);
  @Input() phxTooltipContent?: string | TemplateRef<PhxSliderHandler>;
  @Input() phxTooltipSize?: PhxTooltipSize = PHX_SIZE.SM;
  @Input() phxFillType: PhxFillType = 'progress';

  @Output() readonly phxOnAfterChange = new EventEmitter<PhxSliderValue>();

  value: PhxSliderValue | null = null;
  cacheSliderStart: number | null = null;
  cacheSliderLength: number | null = null;
  activeValueIndex: number | undefined = undefined; // Current activated handle's index ONLY for range=true
  track: { offset: null | number; length: null | number } = { offset: null, length: null }; // Track's offset and length
  handles: PhxSliderHandler[] = []; // Handles' offset
  marksArray: PhxExtendedMark[] | null = null; // "steps" in array type with more data & FILTER out the invalid mark
  bounds: { lower: PhxSliderValue | null; upper: PhxSliderValue | null } = { lower: null, upper: null }; // now for phx-slider-step

  private dragStart$?: Observable<number>;
  private dragMove$?: Observable<number>;
  private dragEnd$?: Observable<Event>;
  private dragStart_?: Subscription | null;
  private dragMove_?: Subscription | null;
  private dragEnd_?: Subscription | null;
  private destroy$ = new Subject<boolean>();
  private isPhxDisableFirstChange = true;

  constructor(
    private sliderService: PhxSliderService,
    private cdr: ChangeDetectorRef,
    private platform: Platform,
  ) {
    super();
  }

  ngOnInit(): void {
    this.handles = generateHandlers(this.phxRange ? 2 : 1);
    this.marksArray = this.phxMarks ? this.generateMarkItems(this.phxMarks) : null;
    this.bindDraggingHandlers();
    this.toggleDragDisabled(this.disabled);

    if (this.getValue() === null) {
      this.setValue(this.formatValue(null));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { disabled, phxMarks, phxRange, phxMin, phxMax, phxStep } = changes;

    if (phxMin) this.phxMin = parseFloat(phxMin.currentValue);
    if (phxMax) this.phxMax = parseFloat(phxMax.currentValue);
    if (phxStep) this.phxStep = parseFloat(phxStep.currentValue);

    if (disabled && !disabled.firstChange) {
      this.toggleDragDisabled(disabled.currentValue);
    } else if (phxMarks && !phxMarks.firstChange) {
      this.marksArray = this.phxMarks ? this.generateMarkItems(this.phxMarks) : null;
    } else if (phxRange && !phxRange.firstChange) {
      this.handles = generateHandlers(phxRange.currentValue ? 2 : 1);
      this.setValue(this.formatValue(null));
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeDrag();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  writeValue(val: PhxSliderValue | null): void {
    this.setValue(val, true);
  }

  onValueChange(_value: PhxSliderValue): void {}

  onTouched(): void {}

  registerOnChange(fn: (value: PhxSliderValue) => void): void {
    this.onValueChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = (this.isPhxDisableFirstChange && this.disabled) || isDisabled;
    this.isPhxDisableFirstChange = false;
    this.toggleDragDisabled(this.disabled);
    this.cdr.markForCheck();
  }

  /**
   * Event handler is only triggered when a slider handler is focused.
   */
  onKeyDown(e: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    const code = e.keyCode;
    const isIncrease = code === RIGHT_ARROW || code === UP_ARROW;
    const isDecrease = code === LEFT_ARROW || code === DOWN_ARROW;

    if (!(isIncrease || isDecrease)) {
      return;
    }

    e.preventDefault();

    let step = isDecrease ? -this.phxStep : this.phxStep;
    const newVal = this.phxRange
      ? (this.value as number[])[this.activeValueIndex!] + step
      : (this.value as number) + step;
    this.setActiveValue(this.ensureNumberInRange(newVal, this.phxMin, this.phxMax));
    this.phxOnAfterChange.emit(this.getValue(true));
  }

  private ensureNumberInRange(num: number, min: number, max: number): number {
    if (isNaN(num) || num < min) {
      return min;
    } else if (num > max) {
      return max;
    } else {
      return num;
    }
  }

  onHandleFocusIn(index: number): void {
    this.activeValueIndex = index;
  }

  private setValue(value: PhxSliderValue | null, isWriteValue: boolean = false): void {
    if (isWriteValue) {
      this.value = this.formatValue(value);
      this.updateTrackAndHandles();
    } else if (!valuesEqual(this.value!, value!)) {
      this.value = value;
      this.updateTrackAndHandles();
      this.onValueChange(this.getValue(true));
    }
  }

  public get minLabel(): string {
    if (!this.phxGetLabelsFromPhxMarks || !this.phxMarks) return this.phxMin.toString();
    const phxMarksArray = Object.values(this.phxMarks);
    return phxMarksArray[0]?.label ?? this.phxMin.toString();
  }

  public get maxLabel(): string {
    if (!this.phxGetLabelsFromPhxMarks || !this.phxMarks) return this.phxMax.toString();
    const phxMarksArray = Object.values(this.phxMarks);
    return phxMarksArray[phxMarksArray.length - 1]?.label ?? this.phxMax.toString();
  }

  private getValue(cloneAndSort: boolean = false): PhxSliderValue {
    if (cloneAndSort && this.value && isValueRange(this.value)) {
      return [...this.value].sort((a, b) => a - b);
    }
    return this.value!;
  }

  /**
   * Clone & sort current value and convert them to offsets, then return the new one.
   */
  private getValueToOffset(value?: PhxSliderValue): PhxSliderValue {
    let normalizedValue = value;

    if (typeof normalizedValue === 'undefined') {
      normalizedValue = this.getValue(true);
    }

    return isValueRange(normalizedValue)
      ? normalizedValue.map(val => this.valueToOffset(val))
      : this.valueToOffset(normalizedValue);
  }

  /**
   * Find the closest value to be activated.
   */
  private setActiveValueIndex(pointerValue: number): void {
    const value = this.getValue();
    if (isValueRange(value)) {
      let minimal: number | null = null;
      let gap: number;
      let activeIndex = -1;
      value.forEach((val, index) => {
        gap = Math.abs(pointerValue - val);
        if (minimal === null || gap < minimal!) {
          minimal = gap;
          activeIndex = index;
        }
      });
      this.activeValueIndex = activeIndex;
    }
  }

  private setActiveValue(pointerValue: number): void {
    if (isValueRange(this.value!)) {
      const newValue = [...(this.value as number[])];
      newValue[this.activeValueIndex!] = pointerValue;
      this.setValue(newValue);
    } else {
      this.setValue(pointerValue);
    }
  }

  /**
   * Update track and handles' position and length.
   */
  private updateTrackAndHandles(): void {
    const value = this.getValue();
    const offset = this.getValueToOffset(value);
    const valueSorted = this.getValue(true);
    const offsetSorted = this.getValueToOffset(valueSorted);
    const boundParts = isValueRange(valueSorted) ? valueSorted : [0, valueSorted];
    const trackParts = isValueRange(offsetSorted)
      ? [offsetSorted[0], offsetSorted[1] - offsetSorted[0]]
      : [0, offsetSorted];

    this.handles.forEach((handle, index) => {
      handle.offset = isValueRange(offset) ? offset[index] : offset;
      handle.value = isValueRange(value) ? value[index] : value || 0;
    });

    [this.bounds.lower, this.bounds.upper] = boundParts;
    [this.track.offset, this.track.length] = trackParts;

    this.cdr.markForCheck();
  }

  private onDragStart(value: number): void {
    this.toggleDragMoving(true);
    this.cacheSliderProperty();
    this.setActiveValueIndex(this.getLogicalValue(value));
    this.setActiveValue(this.getLogicalValue(value));
    this.showHandleTooltip(this.phxRange ? this.activeValueIndex : 0);
  }

  private onDragMove(value: number): void {
    this.setActiveValue(this.getLogicalValue(value));
    this.cdr.markForCheck();
  }

  private getLogicalValue(value: number): number {
    return value;
  }

  private onDragEnd(): void {
    this.phxOnAfterChange.emit(this.getValue(true));
    this.toggleDragMoving(false);
    this.cacheSliderProperty(true);
    this.hideAllHandleTooltip();
    this.cdr.markForCheck();
  }

  /**
   * Create user interactions handles.
   */
  private bindDraggingHandlers(): void {
    if (!this.platform.isBrowser) {
      return;
    }
    const pluckFunc: (keys: string[]) => (event: Event) => number = keys => (event: Event) =>
      keys.reduce((acc: any, key: string) => acc[key] || acc, event);
    const sliderDOM = this.slider.nativeElement;
    const orientField = 'pageX';
    const mouse: any = {
      start: 'mousedown',
      move: 'mousemove',
      end: 'mouseup',
      pluckKey: [orientField]
    };
    const touch: any = {
      start: 'touchstart',
      move: 'touchmove',
      end: 'touchend',
      pluckKey: ['touches', '0', orientField],
      filter: (e: MouseEvent | TouchEvent) => e instanceof TouchEvent
    };

    [mouse, touch].forEach(source => {
      const { start, move, end, pluckKey, filter: filterFunc = () => true } = source;

      source.startPlucked$ = fromEvent(sliderDOM, start).pipe(
        filter(filterFunc),
        tap((e) => {
          e.stopPropagation();
          e.preventDefault();
        }),
        map(pluckFunc(pluckKey)),
        map((position: number) => this.findClosestValue(position))
      );
      source.end$ = fromEvent(document, end);
      source.moveResolved$ = fromEvent(document, move).pipe(
        filter(filterFunc),
        tap((e) => {
          e.stopPropagation();
          e.preventDefault();
        }),
        map(pluckFunc(pluckKey)),
        distinctUntilChanged(),
        map((position: number) => this.findClosestValue(position)),
        distinctUntilChanged(),
        takeUntil(source.end$)
      );
    });

    this.dragStart$ = merge(mouse.startPlucked$!, touch.startPlucked$!) as Observable<number>;
    this.dragMove$ = merge(mouse.moveResolved$!, touch.moveResolved$!) as Observable<number>;
    this.dragEnd$ = merge(mouse.end$!, touch.end$!) as Observable<Event>;
  }

  private subscribeDrag(periods: string[] = ['start', 'move', 'end']): void {
    if (periods.indexOf('start') !== -1 && this.dragStart$ && !this.dragStart_) {
      this.dragStart_ = this.dragStart$.subscribe(this.onDragStart.bind(this));
    }

    if (periods.indexOf('move') !== -1 && this.dragMove$ && !this.dragMove_) {
      this.dragMove_ = this.dragMove$.subscribe(this.onDragMove.bind(this));
    }

    if (periods.indexOf('end') !== -1 && this.dragEnd$ && !this.dragEnd_) {
      this.dragEnd_ = this.dragEnd$.subscribe(this.onDragEnd.bind(this));
    }
  }

  private unsubscribeDrag(periods: string[] = ['start', 'move', 'end']): void {
    if (periods.indexOf('start') !== -1 && this.dragStart_) {
      this.dragStart_.unsubscribe();
      this.dragStart_ = null;
    }

    if (periods.indexOf('move') !== -1 && this.dragMove_) {
      this.dragMove_.unsubscribe();
      this.dragMove_ = null;
    }

    if (periods.indexOf('end') !== -1 && this.dragEnd_) {
      this.dragEnd_.unsubscribe();
      this.dragEnd_ = null;
    }
  }

  private toggleDragMoving(movable: boolean): void {
    const periods = ['move', 'end'];
    if (movable) {
      this.sliderService.isDragging = true;
      this.subscribeDrag(periods);
    } else {
      this.sliderService.isDragging = false;
      this.unsubscribeDrag(periods);
    }
  }

  private toggleDragDisabled(disabled: boolean): void {
    if (disabled) {
      this.unsubscribeDrag();
    } else {
      this.subscribeDrag(['start']);
    }
  }

  private findClosestValue(position: number): number {
    const sliderStart = this.getSliderStartPosition();
    const sliderLength = this.getSliderLength();
    const ratio = this.ensureNumberInRange((position - sliderStart) / sliderLength, 0, 1);
    const val = ((this.phxMax - this.phxMin) * ratio) + this.phxMin;
    const points =
      this.phxMarks === null
        ? []
        : Object.keys(this.phxMarks)
            .map(parseFloat)
            .sort((a, b) => a - b);

    if (this.phxStep !== 0) {
      const closestOne = Math.round(val / this.phxStep) * this.phxStep;
      points.push(closestOne);
    }

    const gaps = points.map(point => Math.abs(val - point));
    const closest = points[gaps.indexOf(Math.min(...gaps))];

    return this.phxStep === 0 ? closest : parseFloat(closest.toFixed(this.getPrecision(this.phxStep)));
  }

  private valueToOffset(value: number): number {
    return this.getPercent(this.phxMin, this.phxMax, value);
  }

  getPrecision(num: number): number {
    const numStr = num.toString();
    const dotIndex = numStr.indexOf('.');
    return dotIndex >= 0 ? numStr.length - dotIndex - 1 : 0;
  }

  getPercent(min: number, max: number, value: number): number {
    return ((value - min) / (max - min)) * 100;
  }

  private getSliderStartPosition(): number {
    if (this.cacheSliderStart !== null) {
      return this.cacheSliderStart;
    }
    const offset = this.getElementOffset(this.slider.nativeElement);
    return offset.left;
  }

  getElementOffset(elem: HTMLElement): PhxSliderStyle {
    if (!elem.getClientRects().length) {
      return { top: 0, left: 0 };
    }

    const rect = elem.getBoundingClientRect();
    const win = elem.ownerDocument!.defaultView;
    return {
      top: rect.top + win!.pageYOffset,
      left: rect.left + win!.pageXOffset
    };
  }

  private getSliderLength(): number {
    if (this.cacheSliderLength !== null) {
      return this.cacheSliderLength;
    }
    const sliderDOM = this.slider.nativeElement;
    return sliderDOM.clientWidth;
  }

  /**
   * Cache DOM layout/reflow operations for performance (may not necessary?)
   */
  private cacheSliderProperty(remove: boolean = false): void {
    this.cacheSliderStart = remove ? null : this.getSliderStartPosition();
    this.cacheSliderLength = remove ? null : this.getSliderLength();
  }

  private formatValue(value: PhxSliderValue | null): PhxSliderValue {
    if (this.isNil(value)) {
      return this.phxRange ? [this.phxMin, this.phxMax] : this.phxMin;
    } else if (assertValueValid(value, this.phxRange)) {
      return isValueRange(value)
        ? value.map(val => this.ensureNumberInRange(val, this.phxMin, this.phxMax))
        : this.ensureNumberInRange(value, this.phxMin, this.phxMax);
    } else {
      return this.phxRange ? [this.phxMin, this.phxMax] : this.phxMin;
    }
  }

  isNil(value: unknown): value is null | undefined {
    return typeof value === 'undefined' || value === null;
  }

  /**
   * Show one handle's tooltip and hide others'.
   */
  private showHandleTooltip(handleIndex: number = 0): void {
    this.handles.forEach((handle, index) => {
      handle.active = index === handleIndex;
    });
  }

  private hideAllHandleTooltip(): void {
    this.handles.forEach(handle => (handle.active = false));
  }

  private generateMarkItems(marks: PhxMarks): PhxExtendedMark[] | null {
    const marksArray: PhxExtendedMark[] = [];
    for (const key in marks) {
      if (marks.hasOwnProperty(key)) {
        const mark = marks[key];
        const val = typeof key === 'number' ? key : parseFloat(key);
        if (val >= this.phxMin && val <= this.phxMax) {
          marksArray.push({ value: val, offset: this.valueToOffset(val), config: mark });
        }
      }
    }
    return marksArray.length ? marksArray : null;
  }
}

function getValueTypeNotMatchError(): Error {
  return new Error(
    `The "phxRange" can't match the "ngModel"'s type, please check these properties: "phxRange", "ngModel".`
  );
}

function isValueRange(value: PhxSliderValue): value is number[] {
  if (value instanceof Array) {
    return value.length === 2;
  } else {
    return false;
  }
}

function generateHandlers(amount: number): PhxSliderHandler[] {
  return Array(amount)
    .fill(0)
    .map(() => ({ offset: null, value: null, active: false }));
}

/**
 * Check if value is valid and throw error if value-type/range not match.
 */
function assertValueValid(value: PhxSliderValue, isRange?: boolean): boolean {
  if ((!isValueRange(value) && isNaN(value)) || (isValueRange(value) && value.some(v => isNaN(v)))) {
    return false;
  }
  return assertValueTypeMatch(value, isRange);
}

/**
 * Assert that if `this.phxRange` is `true`, value is also a range, vice versa.
 */
function assertValueTypeMatch(value: PhxSliderValue, isRange: boolean = false): boolean {
  if (isValueRange(value) !== isRange) {
    throw getValueTypeNotMatchError();
  }
  return true;
}

function valuesEqual(valA: PhxSliderValue, valB: PhxSliderValue): boolean {
  if (typeof valA !== typeof valB) {
    return false;
  }
  return isValueRange(valA) && isValueRange(valB) ? arraysEqual<number>(valA, valB) : valA === valB;
}

function arraysEqual<T>(array1: T[], array2: T[]): boolean {
  if (!array1 || !array2 || array1.length !== array2.length) {
    return false;
  }
}
